import {useState, useEffect} from 'react';
import getAPI from '../functions/API/getAPI.js'
import postAssignTaskInstance from '../functions/API/postAssignTaskInstance.js'
import { toast } from '../lib/toast.js';

export default function AssignTaskInstanceForm({instanceId, setVisible, onAssigned}) {
    const [adminUsers, setAdminUsers] = useState([]);
    const [assigningToMe, setAssigningToMe] = useState(false);
    const [formData, setFormData] = useState({
        owner_admin_user_id: "",
        note: "",
    });

    useEffect(() => {
        async function fetchAdmins() {
            const adminsRes = await getAPI('admin/users');
            if (adminsRes.response.ok && Array.isArray(adminsRes.data)) {
                setAdminUsers(adminsRes.data);
            }
        }
        fetchAdmins();
    }, []);

    async function handleSubmit() {
        if (!formData.owner_admin_user_id) {
            toast.error('Please select an admin user.');
            return;
        }

        const body = {
            id: instanceId,
            owner_admin_user_id: parseInt(formData.owner_admin_user_id),
        };
        if (formData.note) {
            body.note = formData.note;
        }

        const response = await postAssignTaskInstance(body);
        if (response.response.ok) {
            toast.success('Task instance assigned successfully.');
            setVisible(false);
            if (onAssigned) onAssigned();
        } else {
            toast.error('Failed to assign task instance.');
        }
    }

    async function handleAssignToMe() {
        setAssigningToMe(true);
        try {
            const userRes = await getAPI('userdetails');
            if (!userRes.response.ok || !userRes.data?.id) {
                toast.error('Failed to get current user.');
                return;
            }
            const response = await postAssignTaskInstance({
                id: instanceId,
                owner_admin_user_id: userRes.data.id,
            });
            if (response.response.ok) {
                toast.success('Task instance assigned to you.');
                setVisible(false);
                if (onAssigned) onAssigned();
            } else {
                toast.error('Failed to assign task instance.');
            }
        } catch {
            toast.error('Failed to assign task instance.');
        } finally {
            setAssigningToMe(false);
        }
    }

    function handleChange(event) {
        let name = event.target.name;
        let value = event.target.value;
        setFormData({...formData, [name]: value});
    }

    return (
        <form>
            <div className="space-y-8">
                <div>
                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                        Assign Task Instance
                    </h2>
                    <p className="mt-1 text-sm leading-6 text-gray-600">Select an admin user to assign this task to</p>
                </div>

                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-3 md:col-span-1">
                    <div className="sm:col-span-3">
                        <button
                            type="button"
                            onClick={handleAssignToMe}
                            disabled={assigningToMe}
                            className="w-full rounded-md bg-blue-50 px-3 py-2.5 text-sm font-semibold text-blue-700 shadow-sm ring-1 ring-inset ring-blue-200 hover:bg-blue-100 transition-colors disabled:opacity-50"
                        >
                            {assigningToMe ? 'Assigning...' : 'Assign to me'}
                        </button>
                    </div>

                    <div className="sm:col-span-3 relative flex items-center">
                        <div className="flex-grow border-t border-gray-200" />
                        <span className="mx-3 text-xs text-gray-400">or choose an admin</span>
                        <div className="flex-grow border-t border-gray-200" />
                    </div>

                    <div className="sm:col-span-3">
                        <label htmlFor="owner_admin_user_id" className="block text-sm font-medium leading-6 text-gray-900">
                            Admin User
                        </label>
                        <div className="mt-2">
                            <select
                                required
                                onChange={handleChange}
                                name="owner_admin_user_id"
                                id="owner_admin_user_id"
                                value={formData.owner_admin_user_id}
                                className="pl-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                            >
                                <option value="">Select an admin...</option>
                                {adminUsers.map(u => (
                                    <option key={u.id} value={u.id}>{u.firstname} {u.lastname}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="sm:col-span-3">
                        <label htmlFor="note" className="block text-sm font-medium leading-6 text-gray-900">
                            Note (optional)
                        </label>
                        <div className="mt-2">
                            <textarea
                                onChange={handleChange}
                                name="note"
                                id="note"
                                value={formData.note}
                                rows="3"
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-6 flex items-center justify-start gap-x-6">
                <button
                    type="button"
                    onClick={handleSubmit}
                    className="rounded-md bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"
                >
                    Assign
                </button>
                <button type="button" onClick={() => setVisible(false)} className="text-sm font-semibold leading-6 text-gray-900">
                    Cancel
                </button>
            </div>
        </form>
    );
}
